import { useEffect, useRef } from 'react';
import io from 'socket.io-client';
import useStore from '@/store/store';
import { burl } from '@/services/urls';
import { commApi } from './commApi';
import { useCommStore } from './commStore';

// Mounted once for every logged-in user: keeps /comm/me fresh, listens on the
// authenticated "/comm" socket, shows a browser notification for new unread
// messages while the tab is in the background, and hands an existing web push
// subscription to the server so messages can reach this browser when closed.
export const useCommConnection = () => {
	const { user } = useStore();
	const setMe = useCommStore(s => s.setMe);
	const bump = useCommStore(s => s.bump);
	const reset = useCommStore(s => s.reset);
	const lastUnread = useRef(null);

	const cityId = user?.role === 'master' ? user?.cityLocation?.id : undefined;

	useEffect(() => {
		if (!user?.token) {
			reset();
			return undefined;
		}
		let alive = true;

		const refreshMe = async () => {
			const me = await commApi.me(cityId);
			if (!alive || me?.error) return;
			setMe(me);
			const previous = lastUnread.current;
			lastUnread.current = me.unread;
			if (
				previous !== null &&
				me.unread > previous &&
				me.notificationsEnabled &&
				typeof document !== 'undefined' &&
				document.visibilityState === 'hidden' &&
				typeof Notification !== 'undefined' &&
				Notification.permission === 'granted'
			) {
				try {
					new Notification('Νέο μήνυμα', { body: `Έχετε ${me.unread} αδιάβαστα μηνύματα` });
				} catch {
					/* some browsers only allow notifications from a service worker */
				}
			}
		};

		refreshMe();

		const socket = io(`${burl}/comm`, {
			auth: { token: localStorage.getItem('token') },
			transports: ['websocket', 'polling'],
		});
		socket.on('comm:update', data => {
			bump(data);
			refreshMe();
		});
		socket.on('comm:settings', () => {
			bump({ settings: true });
			refreshMe();
		});

		// Hand the browser's existing push subscription (created by the web
		// push login in production) to the Communication Center.
		navigator?.serviceWorker
			?.getRegistration?.()
			.then(reg => reg?.pushManager?.getSubscription())
			.then(sub => sub && commApi.pushSubscription(sub.toJSON ? sub.toJSON() : sub))
			.catch(() => {});

		return () => {
			alive = false;
			socket.disconnect();
		};
	}, [user?.token, user?.id, cityId]);
};
